import React, { useState } from "react";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seoConfig";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Section, Kicker, SerifTitle, HairlineBrass } from "@/components/Section";
import { EditorialImage } from "@/components/EditorialImage";
import { Separator } from "@/components/ui/separator";
import { ArrowUpRight, Check } from "lucide-react";
import {
    CAPABILITIES,
    STUDIOS,
    LIFE_LEGACY_PACKAGES,
    BRAND_BUSINESS_PACKAGES,
    PROCESS_STEPS,
    WHY_BOOK,
    BUSINESS_TRUST_CTA,
} from "@/lib/data";
import {
    useSiteSettings,
    useHomeHero,
    useAboutImage,
    usePortfolio,
    useFineArtPrints,
    useTestimonials,
} from "@/lib/content";
import { PricingCard } from "@/components/Investment";
import { MediaGallery } from "@/components/MediaGallery";

const fade = {
    hidden: { opacity: 0, y: 12, filter: "blur(6px)" },
    show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const FEATURED_CATS = ["All", "Life & Legacy", "Brand & Business", "Portraits", "Motion", "Fine Art"];

const matchesFeaturedCat = (item, cat) => {
    if (cat === "All") return true;
    if (cat === "Life & Legacy") return ["Family", "Portraits", "Events"].includes(item.category);
    if (cat === "Brand & Business") return ["Branding", "Business", "Editorial"].includes(item.category);
    if (cat === "Portraits") return item.category === "Portraits";
    if (cat === "Motion") return ["Editorial", "Events"].includes(item.category);
    if (cat === "Fine Art") return true; // shown from PRINTS separately
    return false;
};

export default function Home() {
    const SITE = useSiteSettings();
    const homeHero = useHomeHero();
    const aboutImage = useAboutImage();
    const PORTFOLIO = usePortfolio();
    const PRINTS = useFineArtPrints();
    const REVIEWS = useTestimonials();
    // Featured testimonials — index lookups against the live REVIEWS array.
    // useMemo so we don't reslice on every keystroke / state change.
    const FEATURED_REVIEWS = React.useMemo(
        () => [REVIEWS[1], REVIEWS[2], REVIEWS[5]].filter(Boolean),
        [REVIEWS],
    );

    const [cat, setCat] = useState("All");
    const filtered =
        cat === "Fine Art"
            ? PRINTS.slice(0, 8).map((p) => ({ id: p.id, title: p.title, image: p.image, category: "Fine Art", isPrint: true }))
            : PORTFOLIO.filter((p) => matchesFeaturedCat(p, cat)).slice(0, 8);

    return (
        <div className="relative">
            <Seo {...SEO.home} />
            {/* ============ HERO ============ */}
            <section data-testid="home-hero" className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
                <img
                    src={homeHero.desktop}
                    srcSet={homeHero.srcSet || undefined}
                    sizes="100vw"
                    alt={homeHero.alt}
                    decoding="async"
                    fetchpriority="high"
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ transform: "scale(1.02)", objectPosition: "center 30%" }}
                />
                {/* Readability scrim — keeps copy legible without dulling the image */}
                <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/55 via-black/15 to-transparent" aria-hidden />
                <div className="absolute inset-x-0 bottom-0 h-[62%] bg-gradient-to-t from-black/72 via-black/35 to-transparent" aria-hidden />
                <div className="absolute inset-0 editorial-vignette" aria-hidden />
                <div className="relative z-10 h-full flex flex-col justify-end">
                    <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-10 pb-16 sm:pb-20 lg:pb-28 text-[hsl(var(--lux-porcelain))]">
                        <motion.h1
                            initial="hidden"
                            animate="show"
                            variants={fade}
                            transition={{ delay: 0.1 }}
                            className="font-serif-display text-[40px] leading-[1.02] sm:text-6xl lg:text-[68px] max-w-[22ch]"
                        >
                            {SITE.heroHeadline}
                        </motion.h1>
                        <motion.p
                            initial="hidden"
                            animate="show"
                            variants={fade}
                            transition={{ delay: 0.25 }}
                            className="mt-5 max-w-[64ch] text-sm sm:text-base leading-[1.7] text-[hsl(var(--lux-porcelain))]/85"
                        >
                            {SITE.heroSubhead}
                        </motion.p>
                        <motion.div
                            initial="hidden"
                            animate="show"
                            variants={fade}
                            transition={{ delay: 0.4 }}
                            className="mt-8 flex flex-wrap items-center gap-3"
                        >
                            <Link to="/inquire" data-testid="home-hero-primary-cta" className="btn-request">
                                Request Availability
                            </Link>
                            <Link to="/portfolio" data-testid="home-hero-portfolio-cta" className="btn-request btn-request--sm btn-request--outline-light">
                                Explore Portfolio
                            </Link>
                        </motion.div>
                        <motion.div
                            initial="hidden"
                            animate="show"
                            variants={fade}
                            transition={{ delay: 0.55 }}
                            className="mt-7 text-[10px] sm:text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--lux-porcelain))]/70"
                        >
                            {SITE.trustLine}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ============ TWO STUDIOS ============ */}
            <Section spacing="loose" id="studios">
                <div className="max-w-[760px]">
                    <Kicker>Two Studios. One House.</Kicker>
                    <SerifTitle className="mt-4">Choose the world you are arriving for.</SerifTitle>
                    <p className="mt-5 text-sm leading-[1.85] text-muted-foreground max-w-[58ch]">
                        Each studio holds its own creative philosophy, portfolio, and process. Both share the same elevated visual language — cinematic, restrained, and intentional.
                    </p>
                </div>

                <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
                    {Object.values(STUDIOS).map((s) => (
                        <Link
                            key={s.id}
                            to={s.slug}
                            data-testid={`studio-card-${s.id}`}
                            className="group relative overflow-hidden rounded-[3px] bg-[hsl(var(--lux-ink))] aspect-[4/5] sm:aspect-[3/4] block"
                        >
                            <img
                                src={s.heroImage}
                                alt={s.name}
                                className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/30" />
                            <div className="relative z-10 h-full flex flex-col justify-end p-7 sm:p-10 text-[hsl(var(--lux-porcelain))]">
                                <div className="kicker text-[hsl(var(--lux-porcelain))]/70">{s.kicker}</div>
                                <h3 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.02] mt-3">{s.name}</h3>
                                <p className="mt-3 text-sm leading-[1.7] text-[hsl(var(--lux-porcelain))]/85 max-w-[44ch]">{s.tagline}</p>
                                <div className="mt-6 inline-flex items-center text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--lux-porcelain))]">
                                    {s.id === "life" ? "Explore Life & Legacy" : "Explore Brand & Business"} <ArrowUpRight className="ml-2 h-4 w-4" />
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </Section>

            {/* ============ BEST WORK FIRST — CURATED PORTFOLIO PREVIEW ============ */}
            <Section spacing="normal" className="bg-[hsl(var(--lux-porcelain))] border-t border-border">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
                    <div>
                        <Kicker>Best Work First</Kicker>
                        <SerifTitle className="mt-3">A curated preview.</SerifTitle>
                    </div>
                    <Link to="/portfolio" className="hidden sm:inline-flex items-center text-xs tracking-[0.18em] uppercase lux-underline">
                        Open the archive <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Link>
                </div>
                <div data-testid="home-featured-filters" className="flex flex-wrap gap-2 pb-6 border-b border-border">
                    {FEATURED_CATS.map((c) => (
                        <button
                            key={c}
                            onClick={() => setCat(c)}
                            data-testid={`home-featured-filter-${c.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                            className={`px-3.5 h-9 rounded-full text-[11px] tracking-[0.18em] uppercase border transition-colors ${
                                cat === c
                                    ? "border-[hsl(var(--lux-oxblood))] text-[hsl(var(--lux-oxblood))] bg-[hsl(var(--lux-oxblood))]/[0.06]"
                                    : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
                {cat === "Fine Art" ? (
                    // Prints stay as click-through cards into /atelier — not a lightbox.
                    <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        {filtered.map((p, i) => (
                            <Link key={p.id} to={`/atelier/${p.id}`} className="group">
                                <EditorialImage
                                    src={p.imageHome || p.image}
                                    srcSet={p.srcSet}
                                    alt={p.alt || p.title}
                                    ratio={i % 2 === 0 ? 4 / 5 : 1}
                                />
                                <div className="mt-2 flex items-center justify-between text-[11px]">
                                    <span>{p.title}</span>
                                    <span className="kicker">{p.category}</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                ) : (
                    // Editorial portfolio — masonry on mobile, 4-col on desktop,
                    // PhotoSwipe v5 lightbox via the unified MediaGallery component.
                    <div className="mt-8">
                        <MediaGallery
                            items={filtered.map((p, i) => ({
                                id: p.id,
                                src: p.imageHome || p.image,
                                alt: p.alt || p.title,
                                caption: p.title,
                                ratio: i % 2 === 0 ? 4 / 5 : 1,
                            }))}
                            desktopColumns={4}
                            initialCount={filtered.length}
                            defaultInquiryType={
                                cat === "Brand & Business" ? "Brand Story Session"
                                : cat === "Life & Legacy" ? "Story Session"
                                : "Story Session"
                            }
                            galleryId="home-best-work-first"
                        />
                    </div>
                )}
            </Section>

            {/* ============ WHY CLIENTS BOOK ============ */}
            <Section spacing="normal" className="border-t border-border">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <Kicker>Why clients book</Kicker>
                        <SerifTitle className="mt-3">A fully guided visual experience.</SerifTitle>
                        <HairlineBrass className="mt-6" />
                        <p className="mt-6 text-sm leading-[1.85] text-muted-foreground max-w-[44ch]">
                            Beautiful photographs are the surface. Underneath is a calm, considered process — designed so you arrive with nothing to figure out on the day.
                        </p>
                    </div>
                    <div className="lg:col-span-7">
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {WHY_BOOK.map((b) => (
                                <li
                                    key={b}
                                    className="flex items-start gap-3 bg-[hsl(var(--lux-porcelain))] border border-border rounded-[2px] p-5"
                                >
                                    <Check className="h-4 w-4 mt-1 text-[hsl(var(--lux-brass))] shrink-0" />
                                    <span className="text-sm leading-[1.6]">{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Section>

            {/* ============ INLINE REVIEW (decision point after Why Book) ============ */}
            <Section spacing="tight" className="border-t border-border">
                <div className="max-w-[820px] mx-auto text-center">
                    <span className="kicker">From a recent client</span>
                    <p className="font-serif-display text-2xl sm:text-3xl leading-[1.4] mt-5">
                        “{REVIEWS[0].quote}”
                    </p>
                    <Separator className="my-7 bg-[hsl(var(--lux-brass))]/50 w-8 mx-auto" />
                    <div className="text-xs tracking-[0.16em] uppercase">{REVIEWS[0].name}</div>
                    <div className="text-xs text-muted-foreground mt-1">{REVIEWS[0].type}</div>
                </div>
            </Section>

            {/* ============ ENTRY POINTS / SIGNATURE OFFERS ============ */}
            <Section spacing="normal" className="bg-[hsl(var(--lux-porcelain))] border-t border-border">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
                    <div>
                        <Kicker>Where to begin</Kicker>
                        <SerifTitle className="mt-3">Signature entry points.</SerifTitle>
                    </div>
                    <p className="text-sm text-muted-foreground max-w-[42ch]">
                        Starting at pricing across both studios — from a polished portrait to a full visual content day. Every session is custom quoted from the starting figure.
                    </p>
                </div>

                {/* LIFE & LEGACY entry tier */}
                <div className="mb-3 flex items-end justify-between">
                    <div>
                        <div className="kicker text-[hsl(var(--lux-oxblood))]">Life &amp; Legacy</div>
                        <h3 className="font-serif-display text-xl sm:text-2xl mt-1 leading-tight">Personal portraits, family stories, couples, proposals, and meaningful life moments.</h3>
                    </div>
                    <Link to="/life-legacy#investment" className="hidden sm:inline-flex items-center text-xs tracking-[0.18em] uppercase lux-underline whitespace-nowrap">
                        View all Life &amp; Legacy pricing <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Link>
                </div>
                <div data-testid="home-life-offers" className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 mt-4">
                    {LIFE_LEGACY_PACKAGES.slice(0, 3).map((p) => (
                        <PricingCard key={p.id} pkg={p} highlighted={!!p.badge} compact />
                    ))}
                </div>
                <div className="mt-4 sm:hidden">
                    <Link to="/life-legacy#investment" className="inline-flex items-center text-xs tracking-[0.18em] uppercase lux-underline">
                        View all Life &amp; Legacy pricing <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Link>
                </div>

                {/* BRAND & BUSINESS entry tier */}
                <div className="mt-14 mb-3 flex items-end justify-between">
                    <div>
                        <div className="kicker text-[hsl(var(--lux-oxblood))]">Brand &amp; Business Studio</div>
                        <h3 className="font-serif-display text-xl sm:text-2xl mt-1 leading-tight max-w-[44ch]">Professional portraits, brand imagery, team headshots, website content, social media visuals, and photo + video content for businesses that want to look trusted, polished, and ready to sell.</h3>
                    </div>
                    <Link to="/brand-business" data-testid="home-brand-explore-link" className="hidden sm:inline-flex items-center text-xs tracking-[0.18em] uppercase lux-underline whitespace-nowrap">
                        Explore Brand &amp; Business <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Link>
                </div>
                <div data-testid="home-brand-offers" className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 mt-4">
                    {BRAND_BUSINESS_PACKAGES.slice(0, 3).map((p) => (
                        <PricingCard key={p.id} pkg={p} highlighted={!!p.badge} compact />
                    ))}
                </div>
                <div className="mt-4 sm:hidden">
                    <Link to="/brand-business" className="inline-flex items-center text-xs tracking-[0.18em] uppercase lux-underline">
                        Explore Brand &amp; Business <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Link>
                </div>

                <p className="mt-12 text-xs text-muted-foreground max-w-[78ch]">
                    Every session is custom quoted based on creative direction, location, timing, usage, final deliverables, and whether photography, video, prints, or albums are included.
                </p>
            </Section>

            {/* ============ BUSINESS TRUST CTA ============ */}
            <Section spacing="normal" className="bg-[hsl(var(--lux-ink))] text-[hsl(var(--lux-porcelain))] border-t border-border" data-testid="home-business-trust-cta">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <span className="kicker text-[hsl(var(--lux-porcelain))]/70">For founders &amp; growing businesses</span>
                        <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05] max-w-[22ch]">
                            {BUSINESS_TRUST_CTA.headline}
                        </h2>
                        <p className="mt-5 text-sm sm:text-base leading-[1.85] text-[hsl(var(--lux-porcelain))]/80 max-w-[64ch]">
                            {BUSINESS_TRUST_CTA.copy}
                        </p>
                    </div>
                    <div className="lg:col-span-5 lg:text-right">
                        <Link
                            to={`/inquire?type=${encodeURIComponent(BUSINESS_TRUST_CTA.sessionType)}`}
                            data-testid="home-business-trust-cta-button"
                            className="btn-request inline-flex"
                        >
                            {BUSINESS_TRUST_CTA.cta}
                        </Link>
                        <div className="mt-4 text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--lux-porcelain))]/55">
                            Most engagements begin with a short strategy call.
                        </div>
                    </div>
                </div>
            </Section>

            {/* ============ DECISION-POINT REVIEW STRIP (3 cards) ============ */}
            <Section spacing="normal" className="border-t border-border">
                <div className="text-center max-w-[680px] mx-auto">
                    <Kicker>What clients say</Kicker>
                    <SerifTitle className="mt-3">“The photos looked like a magazine.”</SerifTitle>
                </div>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                    {FEATURED_REVIEWS.map((r) => (
                        <article key={r.id} data-testid="testimonial-card" className="bg-[hsl(var(--lux-porcelain))] border border-border rounded-[2px] p-7">
                            <span className="font-serif-display text-4xl leading-none text-[hsl(var(--lux-brass))]">“</span>
                            <p className="font-serif-display text-lg leading-[1.45] mt-2">{r.quote}</p>
                            <Separator className="my-5 bg-[hsl(var(--lux-brass))]/50 w-8" />
                            <div className="text-xs tracking-[0.16em] uppercase">{r.name}</div>
                            <div className="text-xs text-muted-foreground mt-1">{r.type}</div>
                        </article>
                    ))}
                </div>
                <div className="mt-10 text-center">
                    <Link to="/reviews" className="inline-flex items-center text-xs tracking-[0.18em] uppercase lux-underline">
                        Read more client stories <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Link>
                </div>
            </Section>

            {/* ============ PROCESS ============ */}
            <Section spacing="normal" className="bg-[hsl(var(--lux-porcelain))] border-t border-border">
                <div className="max-w-[760px]">
                    <Kicker>The Experience</Kicker>
                    <SerifTitle className="mt-3">A calm, considered process.</SerifTitle>
                    <p className="mt-5 text-sm text-muted-foreground max-w-[60ch]">
                        Every engagement follows the same four steps — quiet preparation, intentional direction, and a polished delivery.
                    </p>
                </div>
                <div data-testid="process-steps" className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-5">
                    {PROCESS_STEPS.map((p) => (
                        <div key={p.n} className="bg-background border border-border rounded-[2px] p-7">
                            <div className="kicker">{p.n}</div>
                            <div className="font-serif-display text-xl mt-2">{p.t}</div>
                            <Separator className="my-4 bg-[hsl(var(--lux-brass))]/40 w-8" />
                            <p className="text-sm text-muted-foreground leading-[1.7]">{p.c}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* ============ CAPABILITIES (5x2) ============ */}
            <Section spacing="normal" className="border-t border-border">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
                    <div className="lg:col-span-5">
                        <Kicker>Capabilities</Kicker>
                        <SerifTitle className="mt-3">An end‑to‑end visual house.</SerifTitle>
                        <HairlineBrass className="mt-6" />
                    </div>
                    <div className="lg:col-span-7 lg:pl-10">
                        <p className="text-base leading-[1.85] text-foreground/85 max-w-[60ch]">
                            From concept to final gallery, the studio operates as a single creative team across stills, motion, print, and brand narrative.
                        </p>
                    </div>
                </div>
                <ul data-testid="capabilities-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 border-t border-border">
                    {CAPABILITIES.map((c, i) => (
                        <li
                            key={c.title}
                            data-testid={`capability-${c.n}`}
                            className="group relative px-5 py-7 border-b border-r border-border last:border-r-0 lg:[&:nth-child(5n)]:border-r-0 [&:nth-child(2n)]:sm:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r"
                        >
                            <div className="kicker">{c.n}</div>
                            <h3 className="font-serif-display text-xl leading-tight mt-2">{c.title}</h3>
                            <p className="mt-3 text-[12.5px] leading-[1.65] text-muted-foreground opacity-90">
                                {c.desc}
                            </p>
                            {/* Brass accent on hover */}
                            <span className="absolute left-0 top-0 h-px w-0 bg-[hsl(var(--lux-brass))] transition-[width] duration-500 ease-out group-hover:w-full" />
                        </li>
                    ))}
                </ul>
            </Section>

            {/* ============ THE ATELIER ============ */}
            <Section spacing="normal" className="bg-[hsl(var(--lux-porcelain))] border-t border-border">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-10">
                    <div className="lg:col-span-6">
                        <Kicker>The Atelier</Kicker>
                        <SerifTitle className="mt-3">Fine‑art photography prints for the rooms you live in.</SerifTitle>
                    </div>
                    <div className="lg:col-span-6 flex flex-col lg:flex-row gap-3 lg:items-end lg:justify-end">
                        <Link to="/atelier" className="btn-request btn-request--sm">Shop Fine‑Art Prints</Link>
                        <Link to="/inquire?type=Custom%20VIP%20Experience" className="btn-request btn-request--sm btn-request--ghost">Request a Custom Print Consultation</Link>
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                    {PRINTS.slice(0, 4).map((p) => (
                        <Link key={p.id} to={`/atelier/${p.id}`} className="group">
                            <EditorialImage src={p.image} alt={p.title} ratio={4 / 5} />
                            <div className="mt-3">
                                <div className="font-serif-display text-lg leading-tight">{p.title}</div>
                                <div className="kicker mt-1">{p.edition}</div>
                                <div className="mt-2 text-xs text-muted-foreground">Starting at {p.startingAt}</div>
                            </div>
                        </Link>
                    ))}
                </div>
            </Section>

            {/* ============ MENTORING ============ */}
            <Section spacing="normal" className="border-t border-border">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <Kicker>Studio School · Mentoring</Kicker>
                        <SerifTitle className="mt-3 max-w-[20ch]">A school inside the house.</SerifTitle>
                        <p className="mt-5 text-sm leading-[1.85] text-muted-foreground max-w-[58ch]">
                            For photographers and creatives who want to develop stronger taste, posing, visual identity, pricing, premium positioning, and a more profitable creative business.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <Link to="/mentoring" className="btn-request btn-request--sm">Apply for Mentoring</Link>
                            <Link to="/mentoring" className="btn-request btn-request--sm btn-request--ghost">View Studio School</Link>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <EditorialImage src={aboutImage.desktop} alt={aboutImage.alt || "Studio School"} ratio={4 / 5} />
                    </div>
                </div>
            </Section>

            {/* ============ FINAL CONVERSION ============ */}
            <Section spacing="loose" className="border-t border-border bg-[hsl(var(--lux-ink))] text-[hsl(var(--lux-porcelain))]">
                <div className="max-w-[760px] mx-auto text-center">
                    <span className="kicker text-[hsl(var(--lux-porcelain))]/65">The next step</span>
                    <h2 className="font-serif-display text-3xl sm:text-5xl leading-[1.05] mt-4">
                        Ready to create images that feel like your next level?
                    </h2>
                    <p className="mt-6 text-sm sm:text-base leading-[1.85] text-[hsl(var(--lux-porcelain))]/85">
                        Whether you are preserving a personal story, elevating your professional brand, or building a stronger business presence — your session is guided from concept to final gallery.
                    </p>
                    <div className="mt-9 flex flex-wrap justify-center gap-3">
                        <Link to="/inquire" data-testid="home-final-cta" className="btn-request btn-request--light">
                            Request Availability
                        </Link>
                        <Link to="/portfolio" className="btn-request btn-request--sm btn-request--outline-light">
                            View Portfolio
                        </Link>
                    </div>
                    <p className="mt-7 text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--lux-porcelain))]/55">
                        {SITE.availabilityNote}
                    </p>
                </div>
            </Section>
        </div>
    );
}
